import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import useFetch from 'use-http';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function VaccinationsTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);

  const { get: getVaccinations } = useFetch('/UserAccount/VaccinationsList');
  const { get: getVaccination } = useFetch('/UserAccount/Vaccination');

  const fetchData = async (
    date = dateRange,
    term = search,
    filter = selectedFilter,
  ) => {
    setIsLoading(true);
    sessionDispatch({ type: 'UPDATE', data: { dateFilter: date } });

    const result = await getVaccinations(
      getQueryString(date, term, null, filter, userState.espbiEsi),
    );

    const mapped = _.orderBy(
      result?.vaccinations ?? [],
      ['occurrenceDateTime'],
      ['desc'],
    );
    setData(mapped);

    const dropdownOptions = (result?.filters ?? []).map((filter) => ({
      value: filter,
      label: capitalizeFirst(filter),
    }));
    setFilters(dropdownOptions);
    setIsLoading(false);
  };

  const handleSelection = async (obj) => {
    setIsLoading(true);
    const query = userState.espbiEsi
      ? `?id=${obj.id}&espbiEsi=${userState.espbiEsi}`
      : `?id=${obj.id}`;
    const res = await getVaccination(query);
    history.push('/userAccount/vaccinations/details', {
      data: res,
      status: obj.e063Status,
    });
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, userState?.espbiEsi]);

  return (
    <DataTable
      title="Skiepai"
      loading={isLoading}
      columns={columns({ handleSelection })}
      data={data}
      filterableColumns={[
        'name',
        'manufacturer',
        'e063StatusDisplay',
        'occurrenceDateTime',
      ]}
      onRowClick={(row) => handleSelection(row)}
    />
  );
}

const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const columns = ({ handleSelection }) => [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="SKIEPAS" />
    ),
    cell: ({ row }) => (
      <span
        className="font-medium max-w-[400px] truncate cursor-pointer capitalize"
      >
        {row.original.name}
      </span>
    ),
  },
  {
    accessorKey: 'occurrenceDateTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const rawDate = new Date(row.original.occurrenceDateTime);
      const isMidnight =
        rawDate.getUTCHours() === 0 && rawDate.getUTCMinutes() === 0;
      return (
        <span className="text-muted-foreground">
          {isMidnight
            ? format(rawDate, 'yyyy-MM-dd')
            : format(rawDate, 'yyyy-MM-dd HH:mm')}
        </span>
      );
    },
  },
  {
    accessorKey: 'manufacturer',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="GAMINTOJAS" />
    ),
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.manufacturer}</span>
    ),
  },
  {
    accessorKey: 'e063StatusDisplay',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="BŪSENA" />
    ),
    cell: ({ row }) => {
      const rawStatus = row.original.e063StatusDisplay ?? '';
      const status =
        rawStatus.toLowerCase() === 'darbinis'
          ? 'Nepasirašytas'
          : capitalizeFirst(rawStatus);
      return <Badge variant="neutral">{status}</Badge>;
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelection(row.original);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];
